import React, { useCallback, useMemo } from 'react'

const ResourcesPagination = ({ currentPage, totalPages, onPageChange, isLoading }) => {
  const visiblePages = useMemo(() => {
    const pages = []
    const maxVisible = 7
    
    // Parse numbers to ensure we're working with integers
    const currentPageNum = parseInt(currentPage, 10)
    const totalPagesNum = parseInt(totalPages, 10)
    
    // Validate inputs
    if (isNaN(currentPageNum) || isNaN(totalPagesNum) || totalPagesNum <= 0) {
      return []
    }

    if (totalPagesNum <= maxVisible) {
      for (let i = 1; i <= totalPagesNum; i++) {
        pages.push(i)
      }
    } else {
      pages.push(1)

      if (currentPageNum <= 4) {
        for (let i = 2; i <= Math.min(5, totalPagesNum - 1); i++) {
          pages.push(i)
        }
        if (totalPagesNum > 5) {
          pages.push('...')
        }
        if (totalPagesNum > 1) {
          pages.push(totalPagesNum)
        }
      } else if (currentPageNum >= totalPagesNum - 3) {
        if (totalPagesNum > 5) {
          pages.push('...')
        }
        for (let i = Math.max(2, totalPagesNum - 4); i <= totalPagesNum; i++) {
          pages.push(i)
        }
      } else {
        pages.push('...')
        for (let i = Math.max(2, currentPageNum - 1); i <= Math.min(currentPageNum + 1, totalPagesNum - 1); i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPagesNum)
      }
    }

    return pages
  }, [currentPage, totalPages])

  const handlePageClick = useCallback((page) => {
    const pageNum = parseInt(page, 10)
    const currentPageNum = parseInt(currentPage, 10)
    const totalPagesNum = parseInt(totalPages, 10)

    // Validate page number is within valid range
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPagesNum && pageNum !== currentPageNum && !isLoading) {
      onPageChange(pageNum)

      const resultsElement = document.querySelector('.resources__grid')
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }, [currentPage, totalPages, onPageChange, isLoading])

  const handlePrevious = useCallback(() => {
    const currentPageNum = parseInt(currentPage, 10)

    if (currentPageNum > 1 && !isLoading) {
      const prevPage = currentPageNum - 1
      handlePageClick(prevPage)
    }
  }, [currentPage, handlePageClick, isLoading])

  const handleNext = useCallback(() => {
    const currentPageNum = parseInt(currentPage, 10)
    const totalPagesNum = parseInt(totalPages, 10)

    if (currentPageNum < totalPagesNum && !isLoading) {
      const nextPage = currentPageNum + 1
      handlePageClick(nextPage)
    }
  }, [currentPage, totalPages, handlePageClick, isLoading])

  const getPageUrl = useCallback((page) => {
    if (typeof page !== 'number') return '#'

    const url = new URL(window.location)
    if (page === 1) {
      url.searchParams.delete('page')
    } else {
      url.searchParams.set('page', page.toString())
    }
    return url.toString()
  }, [])

  if (totalPages <= 1) return null

  const currentPageNum = parseInt(currentPage, 10)
  const totalPagesNum = parseInt(totalPages, 10)
  const isFirstPage = currentPageNum === 1
  const isLastPage = currentPageNum === totalPagesNum

  return (
    <nav className="resources__pagination" role="navigation" aria-label="Pagination">
      <ul className="resources__pagination--list">
        {/* Only show prev button if not on first page */}
        {!isFirstPage && (
          <li className="resources__pagination--item">
            <button
              className="resources__pagination--link resources__pagination--link--prev"
              onClick={handlePrevious}
              disabled={isLoading}
              aria-label="Go to previous page"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="sr-only">Previous</span>
            </button>
          </li>
        )}

        {visiblePages.map((page, index) => (
          <li key={`page-${index}`} className="resources__pagination--item">
            {page === '...' ? (
              <span className="resources__pagination--ellipsis" aria-hidden="true">
                &hellip;
              </span>
            ) : page === currentPageNum ? (
              <span 
                className="resources__pagination--link resources__pagination--link--current"
                aria-current="page"
              >
                {page}
              </span>
            ) : (
              <button
                className="resources__pagination--link"
                onClick={() => handlePageClick(page)}
                disabled={isLoading}
                aria-label={`Go to page ${page}`}
              >
                {page}
              </button>
            )}
          </li>
        ))}

        {/* Only show next button if not on last page */}
        {!isLastPage && (
          <li className="resources__pagination--item">
            <button
              className="resources__pagination--link resources__pagination--link--next"
              onClick={handleNext}
              disabled={isLoading}
              aria-label="Go to next page"
            >
              <span className="sr-only">Next</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </li>
        )}
      </ul>

      {isLoading && (
        <div className="resources__pagination--loading" aria-hidden="true">
          <div className="resources__pagination--spinner"></div>
        </div>
      )}
    </nav>
  )
}

export default ResourcesPagination
